import React, { useEffect, useRef, useState } from "react";
import api, { errText } from "@/lib/api";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import useGrabador from "@/hooks/useGrabador";
import OndaAudio from "@/components/OndaAudio";
import { Send, Mic, Square, Paperclip, FileText, Image as ImageIcon, Check, Loader2, ChevronDown, RotateCw, X, Info } from "lucide-react";
import TarjetaChat from "@/components/TarjetaChat";
import MicTooltip from "@/components/MicTooltip";
import { useAuth } from "@/context/AuthContext";
import track from "@/lib/track";

const ACEPTA = ".xlsx,.xls,.csv,.pdf,.doc,.docx,.txt,.zip,.png,.jpg,.jpeg,.webp";

function Puntitos() {
  return (
    <div className="flex gap-1.5 items-center py-1" data-testid="chat-typing">
      <span className="w-1.5 h-1.5 rounded-full bg-dorado animate-pulse" />
      <span className="w-1.5 h-1.5 rounded-full bg-dorado animate-pulse" style={{ animationDelay: "150ms" }} />
      <span className="w-1.5 h-1.5 rounded-full bg-dorado animate-pulse" style={{ animationDelay: "300ms" }} />
    </div>
  );
}

export default function Chat({ contexto, procesoId = null, onAcciones, onEnviar, onAvanzar = null, placeholder = "Contame, o mandame una captura…", compact = false, modo = null, permitirAdjuntar = false, capturaVisible = false, inyectar = null, onCerrar = null, pasoContexto = null, grabarNonce = null }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [booted, setBooted] = useState(false);
  const [transcribiendo, setTranscribiendo] = useState(false);
  const [adjuntos, setAdjuntos] = useState([]);
  const [pendientes, setPendientes] = useState([]); // archivos adjuntados esperando a que se toque enviar
  const [subiendo, setSubiendo] = useState(false);
  const [mostrarBajar, setMostrarBajar] = useState(false);
  const [tarjeta, setTarjeta] = useState(null);
  const [ofrecerCerrar, setOfrecerCerrar] = useState(false);
  const [errorEnvio, setErrorEnvio] = useState(false);
  const [tip, setTip] = useState(false);
  const [capTip, setCapTip] = useState(false); // globito con la instrucción de cómo sacar una captura
  const { user, setUser } = useAuth();
  const g = useGrabador();
  const enviadoRef = useRef(false);
  const taRef = useRef(null);
  const fileRef = useRef(null);
  const listRef = useRef(null);
  const autoRef = useRef(true);
  const sendingRef = useRef(false);
  const colaRef = useRef([]);
  const idRef = useRef(0);
  const nuevoId = () => `m${Date.now()}_${idRef.current++}`;
  // Borrador: lo que se está escribiendo se guarda por chat; sobrevive a recargas y cortes de internet.
  const draftKey = `olimpo:chatdraft:${contexto}:${procesoId || "x"}`;
  const RED = "Se me cortó a mí, no perdiste nada. Tocá reintentar.";

  // Al montar, recuperamos el borrador si había algo escrito sin mandar.
  useEffect(() => {
    try {
      const d = localStorage.getItem(draftKey);
      if (d) setInput(d);
    } catch (e) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  const guardarDraft = (v) => { try { if (v && v.trim()) localStorage.setItem(draftKey, v); else localStorage.removeItem(draftKey); } catch (e) {} };
  const limpiarDraft = () => { try { localStorage.removeItem(draftKey); } catch (e) {} };

  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = Math.min(el.scrollHeight, 148) + "px"; // máximo ~6 líneas
  }, [input]);

  const irAbajo = () => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    autoRef.current = true;
    setMostrarBajar(false);
  };
  const onScroll = () => {
    const el = listRef.current;
    if (!el) return;
    const cerca = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    autoRef.current = cerca;
    setMostrarBajar(!cerca);
  };
  // Baja sola al final cuando llega algo nuevo, salvo que la persona haya subido a leer.
  useEffect(() => {
    if (autoRef.current) {
      const t = setTimeout(irAbajo, 50);
      return () => clearTimeout(t);
    } else {
      setMostrarBajar(true);
    }
  }, [messages, loading, subiendo]);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post("/asistente/arranque", { contexto, proceso_id: procesoId, mensaje: "", modo });
        if (!alive) return;
        const hist = (data.historial || []).map((m) => ({
          rol: m.role === "user" ? "usuario" : "asistente", contenido: m.content,
        }));
        const next = [...hist];
        if (data.reply) next.push({ rol: "asistente", contenido: data.reply });
        setMessages(next);
        if (data && Object.prototype.hasOwnProperty.call(data, "tarjeta")) setTarjeta(data.tarjeta);
        onAcciones && onAcciones(data);
        setBooted(true);
      } catch (e) {
        setMessages([{ rol: "asistente", contenido: "Se me trabó algo. Recargá la página y seguimos." }]);
        setBooted(true);
      }
      if (permitirAdjuntar || contexto) {
        try {
          const { data } = await api.get(`/asistente/adjuntos`);
          if (alive) setAdjuntos(data.adjuntos || []);
        } catch (e) { /* noop */ }
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contexto, procesoId]);

  // Hace el POST del mensaje con UN reintento silencioso a los 2s (el mismo indicador de "escribiendo").
  // Devuelve la data si salió bien; tira el error si falló las dos veces.
  const postChat = async (text) => {
    const body = { contexto, proceso_id: procesoId, mensaje: text };
    if (pasoContexto && pasoContexto.paso_num) Object.assign(body, pasoContexto);
    try {
      return await api.post("/asistente/chat", body);
    } catch (e1) {
      await new Promise((r) => setTimeout(r, 2000));
      return await api.post("/asistente/chat", body);
    }
  };

  // Procesa un mensaje ya presente en el chat (por id). Marca 'ok' + respuesta, o 'fallo' + red de seguridad.
  const despachar = async (text, msgId) => {
    setLoading(true);
    try {
      const { data } = await postChat(text);
      setMessages((m) => [
        ...m.map((x) => (x.id === msgId ? { ...x, estado: "ok" } : x)),
        { rol: "asistente", contenido: data.reply, cta: data.cta || null, id: nuevoId() },
      ]);
      if (data && Object.prototype.hasOwnProperty.call(data, "tarjeta")) setTarjeta(data.tarjeta);
      if (data?.ofrecer_cerrar) setOfrecerCerrar(true);
      onAcciones && onAcciones(data);
      return true;
    } catch (e) {
      // No se pudo mandar ni con el reintento: devolvemos el texto a la caja con un aviso y sacamos
      // la burbuja para no verlo dos veces. El botón MANDAR queda listo para reintentar.
      setMessages((m) => m.filter((x) => x.id !== msgId));
      setInput((prev) => (prev && prev.trim() ? prev : text));
      guardarDraft(text);
      setErrorEnvio(true);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Corre la cola de mensajes pendientes de a uno (una conversación es secuencial).
  const procesarCola = async (primero) => {
    sendingRef.current = true;
    let cur = primero;
    while (cur) {
      await despachar(cur.text, cur.id);
      cur = colaRef.current.shift() || null;
    }
    sendingRef.current = false;
  };

  const send = async (override) => {
    const text = (typeof override === "string" ? override : input).trim();
    const tienePend = pendientes.length > 0 && typeof override !== "string";
    if (!text && !tienePend) return;
    // Con archivos adjuntos: se suben y viajan JUNTO con el texto, en un mismo mensaje.
    if (tienePend) { await enviarConAdjuntos(text); return; }
    // Un envío desde el cuadro mientras hay otro en vuelo no se duplica (el texto sigue visible).
    if (typeof override !== "string" && sendingRef.current) return;
    // La caja se vacía YA, en el momento de mandar (no cuando llega la respuesta). La burbuja de
    // arriba guarda la copia de lo enviado; si el envío falla, el texto vuelve solo a la caja.
    const id = nuevoId();
    setMessages((m) => [...m, { rol: "usuario", contenido: text, estado: "enviando", id }]);
    if (typeof override !== "string") { setInput(""); limpiarDraft(); }
    setErrorEnvio(false);
    autoRef.current = true;
    onEnviar && onEnviar(text);
    if (sendingRef.current) { colaRef.current.push({ text, id }); return; }
    await procesarCola({ text, id });
  };

  // Sube los archivos adjuntados (sin disparar respuesta) y manda el mensaje con el texto + los ids.
  const enviarConAdjuntos = async (text) => {
    if (sendingRef.current) return;
    const pend = pendientes;
    const meta = pend.map((p) => ({ nombre: p.name, es_imagen: p.es_imagen }));
    setPendientes([]);
    setInput(""); limpiarDraft();
    const id = nuevoId();
    setMessages((m) => [...m, { rol: "usuario", contenido: text, adjuntos: meta, estado: "enviando", id }]);
    autoRef.current = true;
    onEnviar && onEnviar(text || meta.map((x) => x.nombre).join(", "));
    setLoading(true);
    sendingRef.current = true;
    try {
      const ids = [];
      for (const p of pend) {
        const fd = new FormData();
        fd.append("file", p.file);
        fd.append("contexto_tipo", ctxTipo);
        if (ctxId) fd.append("contexto_id", ctxId);
        fd.append("solo_guardar", "true");
        const { data } = await api.post(`/asistente/adjuntar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data?.adjunto?.id) ids.push(data.adjunto.id);
        if (data?.adjunto) setAdjuntos((a) => [...a, data.adjunto]);
      }
      const mensaje = text || "Te adjunté esto, miralo y decime qué ves.";
      const chatBody = { contexto, proceso_id: procesoId, mensaje, adjunto_ids: ids };
      if (pasoContexto && pasoContexto.paso_num) Object.assign(chatBody, pasoContexto);
      const { data } = await api.post("/asistente/chat", chatBody);
      setMessages((m) => [
        ...m.map((x) => (x.id === id ? { ...x, estado: "ok" } : x)),
        { rol: "asistente", contenido: data.reply, cta: data.cta || null, id: nuevoId() },
      ]);
      if (data && Object.prototype.hasOwnProperty.call(data, "tarjeta")) setTarjeta(data.tarjeta);
      onAcciones && onAcciones(data);
    } catch (e) {
      toast.error(errText(e) || "No pude enviar el archivo. Probá de nuevo.");
      setMessages((m) => m.map((x) => (x.id === id ? { ...x, estado: "fallo" } : x)));
    } finally {
      setLoading(false);
      sendingRef.current = false;
    }
  };

  // Reintento con un toque desde la burbuja marcada como no enviada.
  const reintentar = async (msgId) => {
    const msg = messages.find((x) => x.id === msgId);
    if (!msg) return;
    setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, estado: "enviando" } : x)));
    autoRef.current = true;
    if (sendingRef.current) { colaRef.current.push({ text: msg.contenido, id: msgId }); return; }
    await procesarCola({ text: msg.contenido, id: msgId });
  };

  const guardarValorHora = async (vh, origen, confirmado = false) => {
    const { data } = await api.post("/valor-hora", { valor_hora: vh, origen, confirmado });
    if (data?.needs_confirm) return data;  // el valor es extremo: la tarjeta repregunta antes de guardar
    setTarjeta(null);
    const guardado = data.valor_hora ?? vh;
    setMessages((m) => [...m, { rol: "asistente", contenido: `Listo, calculado a USD ${guardado} la hora. Con eso hago las cuentas. Cuando quieras lo cambiás y se recalcula todo.` }]);
    onAcciones && onAcciones({ ...data, valor_hora_set: true });
    return data;
  };

  const inyectRef = useRef(null);
  useEffect(() => {
    if (!inyectar || !inyectar.nonce || !booted) return;
    if (inyectRef.current === inyectar.nonce) return;
    inyectRef.current = inyectar.nonce;
    send(inyectar.texto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inyectar?.nonce, booted]);

  // "Contámelo hablando": desde afuera (la tarjeta de Mostrame tu pantalla) se dispara el micrófono
  // que ya existe en el chat. Solo conectamos el botón, no construimos nada nuevo.
  const grabRef = useRef(null);
  useEffect(() => {
    if (!grabarNonce || !booted) return;
    if (grabRef.current === grabarNonce) return;
    grabRef.current = grabarNonce;
    if (g.estado !== "grabando" && g.estado !== "listo") { try { g.start(); } catch (e) {} }
    setTimeout(() => taRef.current?.focus(), 30);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grabarNonce, booted]);

  const agregarPendiente = (file) => {
    if (!file) return;
    if (adjuntos.length + pendientes.length >= 20) { toast.error("Ya llegaste al máximo de 20 archivos en esta cuenta."); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error("El archivo pesa más de 10 MB. Mandame uno más liviano."); return; }
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const es_imagen = ["png", "jpg", "jpeg", "webp"].includes(ext) || (file.type || "").startsWith("image/");
    const url = es_imagen ? URL.createObjectURL(file) : null;
    setPendientes((p) => [...p, { lid: nuevoId(), file, name: file.name, es_imagen, url }]);
  };
  const quitarPendiente = (lid) => setPendientes((p) => {
    const q = p.find((x) => x.lid === lid);
    if (q?.url) { try { URL.revokeObjectURL(q.url); } catch (e) {} }
    return p.filter((x) => x.lid !== lid);
  });

  // PROMPT 7: pegar una imagen con Ctrl+V / Cmd+V directo en el chat.
  const onPaste = (e) => {
    const items = e.clipboardData?.items || [];
    let peguéImagen = false;
    for (const it of items) {
      if (it.kind === "file" && (it.type || "").startsWith("image/")) {
        const f = it.getAsFile();
        if (f) {
          const ext = (f.type.split("/")[1] || "png").split("+")[0];
          const named = f.name && f.name.includes(".") ? f : new File([f], `captura-${Date.now()}.${ext}`, { type: f.type });
          agregarPendiente(named);
          peguéImagen = true;
        }
      }
    }
    if (peguéImagen) e.preventDefault();
  };

  // PROMPT 7: arrastrar y soltar archivos sobre el chat.
  const [arrastrando, setArrastrando] = useState(false);
  const onDrop = (e) => {
    e.preventDefault();
    setArrastrando(false);
    const files = Array.from(e.dataTransfer?.files || []);
    files.forEach((f) => agregarPendiente(f));
  };

  // "Prefiero contarte algo más": saca el botón de esa invitación y vuelve el foco al cuadro.
  const quitarCta = (msgId) => {
    setMessages((m) => m.map((x) => (x.id === msgId ? { ...x, cta: null } : x)));
    setTimeout(() => taRef.current?.focus(), 30);
  };

  const abrirAdjunto = async (a) => {
    try {
      const { data } = await api.get(`/files/${a.storage_path}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch (e) {
      toast.error("No pude abrir el archivo.");
    }
  };

  useEffect(() => { if (g.error) toast.error(g.error); }, [g.error]);

  useEffect(() => {
    if (g.estado === "listo" && g.blob && !enviadoRef.current) {
      enviadoRef.current = true;
      if (!g.blob.size || g.blob.size < 800) {
        toast.error("No te escuché. Probá de nuevo, hablando cerca del micrófono.");
        enviadoRef.current = false; g.reset(); return;
      }
      (async () => {
        setTranscribiendo(true);
        try {
          const ext = ((g.blob.type && g.blob.type.split("/")[1]) || "webm").split(";")[0];
          const fd = new FormData();
          fd.append("file", g.blob, `audio.${ext}`);
          const { data } = await api.post("/transcribir", fd, { headers: { "Content-Type": "multipart/form-data" } });
          setInput((prev) => (prev ? prev.trim() + " " : "") + data.texto);
          if ((user?.mic_tip_count || 0) < 3) {
            setTip(true);
            api.post("/mic-tip-visto").catch(() => {});
            setUser && setUser((u) => (u ? { ...u, mic_tip_count: (u.mic_tip_count || 0) + 1 } : u));
          }
        } catch (e) {
          toast.error(errText(e));
        } finally {
          setTranscribiendo(false);
          enviadoRef.current = false;
          g.reset();
        }
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [g.estado, g.blob]);

  const grabando = g.estado === "grabando";
  const ultimoAsistente = messages.length > 0 && messages[messages.length - 1].rol === "asistente";
  const ctxTipo = contexto === "general" ? "asistente" : contexto;
  const ctxId = procesoId || "";

  return (
    <div
      className="flex flex-col h-full min-h-0 relative"
      data-testid="chat-asistente"
      onDrop={onDrop}
      onDragOver={(e) => { e.preventDefault(); if (!arrastrando) setArrastrando(true); }}
      onDragLeave={(e) => { if (e.currentTarget === e.target) setArrastrando(false); }}
    >
      {arrastrando && (
        <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl pointer-events-none" style={{ background: "rgba(10,10,10,0.85)", border: "2px dashed rgba(201,168,76,0.6)" }} data-testid="chat-drop-zone">
          <span className="mono-label text-dorado">SOLTÁ ACÁ TU CAPTURA O ARCHIVO</span>
        </div>
      )}
      <div ref={listRef} onScroll={onScroll} className="flex-1 overflow-y-auto px-1 min-h-0">
        {!booted && (
          <div className="mt-4 flex items-center gap-3">
            <Puntitos />
            <span className="mono-label text-gris">EL ASISTENTE ESTÁ LEYENDO TU CONTEXTO…</span>
          </div>
        )}
        {messages.map((m, i) => {
          const mismoLado = i > 0 && messages[i - 1].rol === m.rol;
          return (
            <div key={m.id || i} className={`${i === 0 ? "" : mismoLado ? "mt-1.5" : "mt-4"} ${m.rol === "usuario" ? "flex flex-col items-end" : "flex flex-col items-start"}`}>
              <div
                className={
                  m.rol === "usuario"
                    ? `max-w-[85%] bg-negro2 border rounded-xl rounded-br-sm px-4 py-2.5 text-crema text-[0.95rem] ${m.estado === "fallo" ? "border-rojo/70" : "border-borde"}`
                    : "max-w-[92%] text-crema text-[0.98rem] leading-relaxed"
                }
                data-testid={m.rol === "usuario" ? "msg-usuario" : "msg-asistente"}
              >
                {m.rol === "usuario" ? (
                  <>
                    {(m.adjuntos || []).length > 0 && (
                      <div className="flex flex-wrap gap-1.5 mb-1.5" data-testid="msg-adjuntos">
                        {m.adjuntos.map((a, k) => (
                          <span key={k} className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-negro border border-borde text-xs text-crema">
                            {a.es_imagen ? <ImageIcon size={12} className="text-gris" /> : <FileText size={12} className="text-gris" />}
                            <span className="max-w-[150px] truncate">{a.nombre}</span>
                          </span>
                        ))}
                      </div>
                    )}
                    {m.contenido ? <span style={{ whiteSpace: "pre-wrap" }}>{m.contenido}</span> : null}
                  </>
                ) : (
                  <Markdown>{m.contenido}</Markdown>
                )}
              </div>
              {m.rol === "usuario" && m.estado === "enviando" && (
                <div className="flex items-center gap-1.5 mt-1 text-gris" data-testid="msg-enviando">
                  <Loader2 size={11} className="animate-spin" />
                  <span className="mono-label" style={{ fontSize: "9px", letterSpacing: "0.12em" }}>EN CAMINO</span>
                </div>
              )}
              {m.rol === "usuario" && m.estado === "fallo" && (
                <div className="flex items-center gap-2 mt-1 max-w-[85%]" data-testid="msg-fallo">
                  <span className="text-xs text-gris" data-testid="msg-fallo-texto">{RED}</span>
                  <button
                    onClick={() => reintentar(m.id)}
                    className="flex items-center gap-1 text-xs text-dorado border border-dorado/50 rounded-full px-2.5 py-1 hover:bg-dorado/10 transition-colors shrink-0"
                    data-testid="msg-reintentar"
                    aria-label="Reintentar enviar"
                  >
                    <RotateCw size={12} /> REINTENTAR
                  </button>
                </div>
              )}
              {m.rol === "asistente" && m.cta && i === messages.length - 1 && (
                <div className="flex flex-wrap items-center gap-2.5 mt-3" data-testid="chat-cta">
                  <button
                    onClick={() => onAvanzar && onAvanzar(m.cta.tipo)}
                    className="btn-dorado !py-2 !px-4 text-sm"
                    data-testid="chat-cta-primario"
                  >
                    {m.cta.label}
                  </button>
                  <button
                    onClick={() => quitarCta(m.id)}
                    className="mono-label text-gris hover:text-crema underline transition-colors"
                    style={{ fontSize: "11px" }}
                    data-testid="chat-cta-secundario"
                  >
                    {m.cta.secundario || "Prefiero contarte algo más"}
                  </button>
                </div>
              )}
            </div>
          );
        })}
        {(loading || subiendo) && (
          <div className={`flex justify-start ${ultimoAsistente ? "mt-1.5" : "mt-4"}`}>
            <Puntitos />
          </div>
        )}
      </div>

      {mostrarBajar && (
        <button onClick={irAbajo} className="absolute right-3 bottom-24 z-10 w-9 h-9 rounded-full grad-dorado glow-dorado flex items-center justify-center shadow-lg" data-testid="chat-bajar" aria-label="Bajar al último mensaje">
          <ChevronDown size={18} className="text-negro" />
        </button>
      )}

      {pendientes.length > 0 && (
        <div className="flex flex-wrap gap-2 pt-2" data-testid="chat-pendientes">
          {pendientes.map((p) => (
            <div
              key={p.lid}
              className="flex items-center gap-2 px-2 py-1.5 rounded-lg card-dark text-xs text-crema"
              data-testid={`pendiente-${p.lid}`}
              title={p.name}
            >
              {p.es_imagen && p.url ? (
                <img src={p.url} alt={p.name} className="w-9 h-9 rounded object-cover shrink-0" data-testid={`pendiente-thumb-${p.lid}`} />
              ) : p.es_imagen ? (
                <ImageIcon size={13} className="text-gris" />
              ) : (
                <FileText size={13} className="text-gris" />
              )}
              <span className="max-w-[140px] truncate">{p.name}</span>
              <button
                onClick={() => quitarPendiente(p.lid)}
                className="text-gris hover:text-rojo transition-colors shrink-0"
                data-testid={`pendiente-quitar-${p.lid}`}
                aria-label="Sacar el archivo"
              >
                <X size={14} />
              </button>
            </div>
          ))}
        </div>
      )}

      {tarjeta && (
        <TarjetaChat tarjeta={tarjeta} onGuardarValor={guardarValorHora} />
      )}

      <div className="pt-2 mt-1 border-t border-borde">
        {capturaVisible && (() => {
          const so = (user?.sistema_operativo || "").toLowerCase();
          const esMac = so.includes("mac") || so.includes("apple") || so.includes("os x");
          const esWin = so.includes("win");
          const hoverDevice = typeof window !== "undefined" && window.matchMedia && window.matchMedia("(hover: hover)").matches;
          const instrMac = "Apretá Command + Control + Shift + 4, seleccioná la parte de la pantalla que querés mandar, y pegala acá con Command + V.";
          const instrWin = "Apretá la tecla Windows + Shift + S, seleccioná la parte de la pantalla que querés mandar, y pegala acá con Control + V.";
          return (
            <div className="relative mb-2" data-testid="chat-captura-wrap">
              <button
                onClick={() => fileRef.current?.click()}
                onMouseEnter={hoverDevice ? () => setCapTip(true) : undefined}
                onMouseLeave={hoverDevice ? () => setCapTip(false) : undefined}
                className="btn-dorado w-full flex items-center justify-center gap-2 !py-2.5 text-sm"
                data-testid="chat-mandar-captura"
              >
                <ImageIcon size={16} /> MANDAR UNA CAPTURA
                <span
                  role="button"
                  tabIndex={0}
                  aria-label="Cómo sacar una captura"
                  onClick={(e) => { e.stopPropagation(); setCapTip(true); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); e.stopPropagation(); setCapTip((v) => !v); } }}
                  className="inline-flex items-center justify-center opacity-80 hover:opacity-100"
                  data-testid="chat-captura-ayuda"
                >
                  <Info size={15} />
                </span>
              </button>
              {capTip && (
                <div
                  className="absolute z-50 bottom-full mb-2 left-0 right-0"
                  role="status"
                  data-testid="chat-captura-tooltip"
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <div className="relative rounded-xl px-4 py-3 mx-auto" style={{ maxWidth: 420, background: "#1A1A1A", border: "1px solid #2A2A2A", boxShadow: "0 8px 24px rgba(0,0,0,0.45)" }}>
                    <button
                      onClick={(e) => { e.stopPropagation(); setCapTip(false); }}
                      className="absolute top-1.5 right-1.5 text-gris hover:text-crema transition-colors sm:hidden"
                      data-testid="chat-captura-tooltip-cerrar"
                      aria-label="Cerrar"
                    >
                      <X size={13} />
                    </button>
                    {(esMac || (!esMac && !esWin)) && (
                      <div data-testid="chat-captura-mac" className={(!esMac && !esWin) ? "mb-2" : ""}>
                        {(!esMac && !esWin) && <span className="mono-label text-dorado block mb-0.5" style={{ fontSize: 10 }}>MAC</span>}
                        <span className="block text-crema" style={{ fontSize: 13, lineHeight: 1.4 }}>{instrMac}</span>
                      </div>
                    )}
                    {(esWin || (!esMac && !esWin)) && (
                      <div data-testid="chat-captura-windows">
                        {(!esMac && !esWin) && <span className="mono-label text-dorado block mb-0.5" style={{ fontSize: 10 }}>WINDOWS</span>}
                        <span className="block text-crema" style={{ fontSize: 13, lineHeight: 1.4 }}>{instrWin}</span>
                      </div>
                    )}
                    <span className="absolute block" style={{ width: 10, height: 10, background: "#1A1A1A", transform: "rotate(45deg)", bottom: "-5px", left: "24px", borderRight: "1px solid #2A2A2A", borderBottom: "1px solid #2A2A2A" }} />
                  </div>
                </div>
              )}
            </div>
          );
        })()}
        {errorEnvio && (
          <div className="flex items-center gap-2 mb-2" data-testid="chat-error-envio">
            <span className="text-xs text-rojo">{RED}</span>
          </div>
        )}
        <div className="flex gap-2 items-end">
          <div className="shrink-0 flex items-center gap-2 relative" data-testid="chat-audio-slot">
            <MicTooltip show={tip} onClose={() => setTip(false)} arrow="down" align="left" />
            {transcribiendo ? (
              <div className="w-10 h-10 flex items-center justify-center" data-testid="chat-transcribiendo" title="Transcribiendo…">
                <div className="w-6 h-6 rounded-full grad-dorado glow-dorado animate-breathe" />
              </div>
            ) : grabando ? (
              <>
                <button onClick={g.stop} className="w-10 h-10 rounded-full bg-rojo/90 flex items-center justify-center animate-breathe" data-testid="grabador-terminar" aria-label="Listo, transcribir">
                  <Square size={15} className="text-white" fill="white" />
                </button>
                <div className="hidden sm:flex items-center gap-2">
                  <OndaAudio nivel={g.nivel} />
                  <span className="mono-label text-crema" data-testid="grabador-tiempo">{g.mmss}</span>
                </div>
              </>
            ) : (
              <button onClick={g.start} className="btn-fantasma !px-2.5 !py-2.5" data-testid="chat-microfono" aria-label="Hablar">
                <Mic size={16} />
              </button>
            )}

            {(
              <>
                <input
                  ref={fileRef}
                  type="file"
                  accept={ACEPTA}
                  className="hidden"
                  onChange={(e) => { const f = e.target.files?.[0]; if (f) agregarPendiente(f); e.target.value = ""; }}
                  data-testid="chat-file-input"
                />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="btn-fantasma !px-2.5 !py-2.5"
                  data-testid="chat-adjuntar"
                  aria-label="Adjuntar archivo o captura"
                  title="Adjuntar una captura o un archivo (imagen, PDF, Excel, Word o texto — hasta 10 MB). También podés pegar con Ctrl+V o arrastrar."
                >
                  <Paperclip size={16} />
                </button>
              </>
            )}
          </div>

          <textarea
            ref={taRef}
            value={input}
            onChange={(e) => { setInput(e.target.value); guardarDraft(e.target.value); if (tip) setTip(false); if (errorEnvio) setErrorEnvio(false); }}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
            onPaste={onPaste}
            placeholder={grabando ? "Te estoy escuchando… o escribí acá" : (transcribiendo ? "Pasando tu audio a texto…" : placeholder)}
            rows={2}
            style={{ minHeight: "52px", maxHeight: "148px" }}
            className="input-dark resize-none flex-1 !py-2.5"
            data-testid="chat-input"
          />

          <button onClick={() => send()} disabled={loading || grabando || transcribiendo || (!input.trim() && pendientes.length === 0)} className="btn-dorado !px-4 !py-2.5 shrink-0" data-testid="chat-enviar" aria-label="Enviar">
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
